import type { BriefingView, CardView, CrossFitPayload, NewsPayload } from './api';
import { classifyUnknowns } from './coverage';

/**
 * How an edition becomes a topic board.
 *
 * Every entry of the edition is placed exactly once: CrossFit cards go to the
 * training panel, every news card to exactly one topic group. Nothing is dropped
 * and nothing is re-ranked:
 *
 * - Within a group, the edition's ranking stands: visible entries (the front
 *   page, `in_more = false`) by position, then the longer tail by position.
 * - Groups are ordered by their best (lowest) edition position, so the group
 *   holding the edition's top story comes first. Groups with front-page entries
 *   always precede groups that exist only in the tail.
 * - Cards without a topic form "Weitere Themen", placed after the named topics:
 *   it is a remainder, not an interest the reader chose.
 */

export const NO_TOPIC = '__none';
export const NO_TOPIC_LABEL = 'Weitere Themen';

export interface TopicGroup {
  id: string;
  label: string;
  /** Front-page entries in rank order. */
  visible: CardView[];
  /** Tail entries in rank order, revealed progressively. */
  tail: CardView[];
  /** Lowest edition position in the group; used for group order. */
  best: number;
  total: number;
}

export interface BoardModel {
  groups: TopicGroup[];
  training: CardView[];
  /** Event entries of the edition, in rank order; shown in the agenda block. */
  events: CardView[];
  /**
   * The first edition's background block: stories shown before and unchanged,
   * repeated on purpose. Kept off the topic board so they never read as new.
   */
  context: CardView[];
  /** Every news entry, for counting and tests. */
  newsCount: number;
  sourceCount: number;
}

export function isCrossFit(cv: CardView): boolean {
  const t = cv.version.payload.payload_type;
  return cv.card.section === 'crossfit' || t === 'crossfit.v1' || t === 'crossfit.v2';
}

export function isEvent(cv: CardView): boolean {
  return cv.card.section === 'events' || (cv.version.payload.payload_type as string) === 'event.v1';
}

function position(cv: CardView, fallback: number): number {
  return cv.entry?.position ?? fallback;
}

/** All entries of an edition in rank order, visible and tail, each exactly once. */
export function allEntries(view: BriefingView): CardView[] {
  const seen = new Set<string>();
  const out: Array<{ cv: CardView; pos: number }> = [];
  let i = 0;
  const push = (cv: CardView) => {
    if (seen.has(cv.version.id)) return;
    seen.add(cv.version.id);
    out.push({ cv, pos: position(cv, 1_000_000 + i++) });
  };
  for (const s of view.sections ?? []) for (const cv of s.cards ?? []) push(cv);
  for (const cv of view.more ?? []) push(cv);
  return out.sort((a, b) => a.pos - b.pos).map((x) => x.cv);
}

export function buildBoard(view: BriefingView): BoardModel {
  const entries = allEntries(view);
  const training: CardView[] = [];
  const events: CardView[] = [];
  const context: CardView[] = [];
  const byId = new Map<string, TopicGroup>();
  const sources = new Set<string>();
  let newsCount = 0;

  entries.forEach((cv, idx) => {
    if (isCrossFit(cv)) {
      training.push(cv);
      return;
    }
    if (isEvent(cv)) {
      events.push(cv);
      return;
    }
    if (cv.entry?.section === 'context') {
      context.push(cv);
      return;
    }
    newsCount += 1;
    for (const e of cv.version.evidence ?? []) sources.add(e.source_id);
    const id = cv.topic?.id ?? NO_TOPIC;
    let g = byId.get(id);
    if (!g) {
      g = { id, label: cv.topic?.label ?? NO_TOPIC_LABEL, visible: [], tail: [], best: Infinity, total: 0 };
      byId.set(id, g);
    }
    const pos = position(cv, 1_000_000 + idx);
    g.best = Math.min(g.best, pos);
    g.total += 1;
    // Entries without placement information count as front page: they came
    // from `sections`, which is what the reader was shown first.
    if (cv.entry?.in_more) g.tail.push(cv);
    else g.visible.push(cv);
  });

  const groups = [...byId.values()].sort((a, b) => {
    const aNone = a.id === NO_TOPIC ? 1 : 0;
    const bNone = b.id === NO_TOPIC ? 1 : 0;
    if (aNone !== bNone) return aNone - bNone;
    const aFront = a.visible.length > 0 ? 0 : 1;
    const bFront = b.visible.length > 0 ? 0 : 1;
    if (aFront !== bFront) return aFront - bFront;
    return a.best - b.best || a.label.localeCompare(b.label, 'de-CH');
  });

  return { groups, training, events, context, newsCount, sourceCount: sources.size };
}

/** Distinct source names of a card, in evidence order. */
export function sourceNames(cv: CardView): string[] {
  const names: string[] = [];
  for (const e of cv.version.evidence ?? []) {
    const n = e.source_name || e.source_id;
    if (!names.includes(n)) names.push(n);
  }
  if (names.length === 0 && cv.version.payload.payload_type === 'news.v1') {
    for (const s of (cv.version.payload as NewsPayload).sources ?? []) {
      if (!names.includes(s.source_id)) names.push(s.source_id);
    }
  }
  return names;
}

/** The newest stated publication time among a card's sources, or null. */
export function latestPublished(cv: CardView): string | null {
  let best: number | null = null;
  let bestRaw: string | null = null;
  for (const e of cv.version.evidence ?? []) {
    if (!e.published_at) continue;
    const t = Date.parse(e.published_at);
    if (Number.isNaN(t)) continue;
    if (best === null || t > best) {
      best = t;
      bestRaw = e.published_at;
    }
  }
  return bestRaw;
}

/** Material uncertainty a reader should see on the card itself. */
export function uncertainties(cv: CardView): string[] {
  const out: string[] = [];
  const v = cv.version;
  if (v.degraded) out.push('Ohne Sprachmodell erstellt: zeigt Quellentext statt einer Zusammenfassung.');
  if (v.collection_status === 'partial') out.push('Unvollständig erfasst: nicht alle Quellentexte lagen vor.');
  if (v.collection_status === 'failed') out.push('Abruf fehlgeschlagen: der Inhalt kann veraltet sein.');
  if (v.payload.payload_type === 'news.v1') {
    // Only real open questions; coverage limits are a compact hint of their own.
    for (const q of classifyUnknowns(v.payload as NewsPayload).questions) out.push(`Offen: ${q}`);
  }
  return out;
}

/**
 * The CrossFit card that describes the edition's day: the one for the edition
 * date, else the latest earlier one, else the latest at all. The others stay in
 * the edition and remain reachable from the training panel.
 */
export function pickTraining(training: CardView[], day: string): { primary: CardView | null; others: CardView[] } {
  const withDate = training.map((cv) => ({ cv, date: isCrossFitPayload(cv.version.payload) ? cv.version.payload.local_date : '' }));
  const exact = withDate.find((x) => x.date === day);
  const earlier = withDate.filter((x) => x.date <= day).sort((a, b) => b.date.localeCompare(a.date))[0];
  const latest = [...withDate].sort((a, b) => b.date.localeCompare(a.date))[0];
  const primary = (exact ?? earlier ?? latest)?.cv ?? null;
  const others = withDate
    .filter((x) => x.cv !== primary)
    .sort((a, b) => b.date.localeCompare(a.date))
    .map((x) => x.cv);
  return { primary, others };
}

export function isCrossFitPayload(p: CardView['version']['payload']): p is CrossFitPayload {
  return p.payload_type === 'crossfit.v1' || p.payload_type === 'crossfit.v2';
}
